
import React, { useState, useEffect } from 'react';
import { 
  ArrowRight, Building2, Users, Share2, Sparkles, CheckCircle2, Shield, 
  Globe, ChevronDown, Zap, Utensils, 
  Camera, Truck, Palette, Star, ChevronLeft, Lock, FileText, 
  HelpCircle, BookOpen, Layers, Newspaper, CreditCard, DollarSign,
  Heart, TrendingUp, Award, Info, Mail, MessageSquare, Book, ShieldCheck, 
  Search, Play, Download
} from 'lucide-react';
import Logo from './Logo';
import { COUNTRIES_CURRENCIES, BASE_PRICING } from '../constants';
import { useLanguage } from '../contexts/LanguageContext';
import { SUPPORTED_LANGUAGES, Language } from '../i18n/translations';

interface LandingPageProps {
  onGetStarted: () => void;
  extraServices?: string[];
  pricing?: { individual: number, business: number };
}

type PageId = 'home' | 'features' | 'integrations' | 'pricing' | 'updates' | 'help-center' | 'community' | 'blog' | 'guides' | 'about-us' | 'privacy' | 'terms';

const LandingPage: React.FC<LandingPageProps> = ({ 
  onGetStarted, 
  extraServices = [], 
  pricing = { individual: BASE_PRICING.INDIVIDUAL, business: BASE_PRICING.BUSINESS } 
}) => {
  const [activePage, setActivePage] = useState<PageId>('home');
  const [pricingCountry, setPricingCountry] = useState('Saudi Arabia');
  const [scrolled, setScrolled] = useState(false);
  const { t, language, setLanguage } = useLanguage();
  
  const selectedCurrencyConfig = COUNTRIES_CURRENCIES.find(c => c.country === pricingCountry) || COUNTRIES_CURRENCIES[0];
  const localIndPrice = (pricing.individual * selectedCurrencyConfig.rate).toFixed(2);
  const localBizPrice = (pricing.business * selectedCurrencyConfig.rate).toFixed(2);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activePage]);

  const handleLanguageChange = (code: Language) => {
    setLanguage(code);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Event Manager Pro',
          text: 'Orchestrate your dream celebration with precision.',
          url: window.location.href,
        });
      } catch (error) { console.log('Error sharing:', error); }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const baseServices = [
    { icon: Building2, title: t('landing.services.hall'), desc: t('landing.services.hall.desc'), color: 'text-wedding-600', bg: 'bg-wedding-100' },
    { icon: Utensils, title: t('landing.services.catering'), desc: t('landing.services.catering.desc'), color: 'text-rose-600', bg: 'bg-rose-100' },
    { icon: Camera, title: t('landing.services.photo'), desc: t('landing.services.photo.desc'), color: 'text-blue-600', bg: 'bg-blue-100' },
    { icon: Truck, title: t('landing.services.transport'), desc: t('landing.services.transport.desc'), color: 'text-purple-600', bg: 'bg-purple-100' },
    { icon: Palette, title: t('landing.services.deco'), desc: t('landing.services.deco.desc'), color: 'text-pink-500', bg: 'bg-pink-100' },
  ];

  const dynamicServices = extraServices
    .filter(s => !baseServices.some(bs => bs.title.includes(s.split(' ')[0])))
    .map(s => ({
      icon: Star,
      title: s,
      desc: "Professional services tailored for your celebration.",
      color: 'text-indigo-600',
      bg: 'bg-indigo-100'
    }));

  const allServices = [...baseServices, ...dynamicServices];

  const renderStaticPage = (id: PageId) => {
    const pageContent: Record<string, { title: string, subtitle: string, icon: any, content: React.ReactNode }> = {
      features: {
        title: "Key Features",
        subtitle: "A comprehensive suite of tools for event excellence.",
        icon: Zap,
        content: (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
            {[
              { t: "AI Event Architect", d: "Gemini-powered theme generation, vendor email drafting, and event schedule construction.", i: Sparkles },
              { t: "Vendor Nexus", d: "Discovery engine for top-rated local services with verified ratings and location data.", i: Search },
              { t: "Unified CRM", d: "Manage inquiries, bookings, and client preferences in one professional dashboard.", i: Users },
              { t: "Budget Optimization", d: "Real-time cost tracking with multi-currency support and regional pricing.", i: DollarSign }
            ].map((f, i) => (
              <div key={i} className="flex gap-4 p-5 md:p-6 rounded-2xl bg-white border border-wedding-100 shadow-sm">
                <div className="shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-xl bg-wedding-50 flex items-center justify-center text-wedding-600">
                  <f.i className="w-5 h-5 md:w-6 md:h-6" />
                </div>
                <div>
                   <h3 className="font-bold text-gray-900 mb-1 text-sm md:text-base">{f.t}</h3>
                   <p className="text-gray-500 text-xs md:text-sm leading-relaxed">{f.d}</p>
                </div>
              </div>
            ))}
          </div>
        )
      },
      integrations: {
        title: "Integrations",
        subtitle: "Seamlessly connecting with the tools you already use.",
        icon: Layers,
        content: (
          <div className="space-y-4 md:space-y-6">
            {[
              { t: "Google Workspace", d: "Sync event dates with Google Calendar and location data with Google Maps.", i: Globe },
              { t: "Secure Payments", d: "Stripe and regional payment gateway integrations for automated invoicing.", i: CreditCard },
              { t: "Communication Hub", d: "Connect with WhatsApp and email services for instant client notifications.", i: MessageSquare }
            ].map((item, i) => (
              <div key={i} className="flex items-center p-5 md:p-6 bg-white border border-wedding-100 rounded-2xl shadow-sm">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl bg-gray-50 flex items-center justify-center text-wedding-500 mr-4 md:mr-6">
                  <item.i className="w-6 h-6 md:w-8 md:h-8" />
                </div>
                <div>
                   <h3 className="font-bold text-gray-900 text-base md:text-lg">{item.t}</h3>
                   <p className="text-gray-500 text-xs md:text-sm">{item.d}</p>
                </div>
              </div>
            ))}
          </div>
        )
      },
      pricing: {
        title: "Investment Plans",
        subtitle: "Simple plans designed to scale with your ambition.",
        icon: DollarSign,
        content: (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
             <div className="p-8 md:p-10 border border-wedding-100 rounded-[2rem] md:rounded-[2.5rem] bg-white shadow-xl text-center">
                <span className="inline-block px-3 py-1 bg-green-100 text-green-700 text-[10px] font-black uppercase rounded-full mb-4">Best for Planners</span>
                <h3 className="text-xl font-bold text-gray-400 uppercase tracking-widest mb-2">Individual</h3>
                <p className="text-5xl md:text-6xl font-black text-gray-900 mb-4">{selectedCurrencyConfig.symbol}{localIndPrice}</p>
                <p className="text-sm text-gray-400 mb-8 uppercase font-bold tracking-widest">Per event / 1st free</p>
                <button onClick={onGetStarted} className="w-full py-4 bg-wedding-600 text-white rounded-xl font-bold uppercase shadow-lg hover:bg-wedding-700 transition-all">Get Started</button>
             </div>
             <div className="p-8 md:p-10 border border-gray-900 rounded-[2rem] md:rounded-[2.5rem] bg-gray-900 text-white shadow-2xl text-center">
                <span className="inline-block px-3 py-1 bg-wedding-600 text-white text-[10px] font-black uppercase rounded-full mb-4">Enterprise Node</span>
                <h3 className="text-xl font-bold text-gray-500 uppercase tracking-widest mb-2">Venue Pro</h3>
                <p className="text-5xl md:text-6xl font-black text-white mb-4">{selectedCurrencyConfig.symbol}{localBizPrice}</p>
                <p className="text-sm text-gray-500 mb-8 uppercase font-bold tracking-widest">Per month / 30 day trial</p>
                <button onClick={onGetStarted} className="w-full py-4 bg-white text-gray-900 rounded-xl font-bold uppercase shadow-lg hover:bg-gray-100 transition-all">Go Professional</button>
             </div>
          </div>
        )
      },
      updates: {
        title: "Product Updates",
        subtitle: "Continuous innovation for your event operations.",
        icon: Newspaper,
        content: (
          <div className="space-y-6 md:space-y-8">
            {[
              { v: "v3.4.0", d: "May 2024", t: "AI Vision Boards", p: "New AI tool to generate visual moodboards from text descriptions." },
              { v: "v3.3.0", d: "April 2024", t: "Multi-Currency Node", p: "Enhanced financial tracking for international venue chains." }
            ].map((upd, i) => (
              <div key={i} className="p-5 md:p-6 bg-white border border-wedding-100 rounded-2xl relative">
                <span className="absolute top-4 md:top-6 right-4 md:right-6 text-[10px] md:text-xs font-black text-wedding-600 bg-wedding-50 px-2 py-1 rounded-md">{upd.v}</span>
                <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">{upd.d}</p>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{upd.t}</h3>
                <p className="text-gray-500 text-xs md:text-sm leading-relaxed">{upd.p}</p>
              </div>
            ))}
          </div>
        )
      }
    };

    const active = pageContent[id] || { 
      title: "Information", 
      subtitle: "Overview of our platform services.", 
      icon: Info, 
      content: <p className="text-center text-gray-500 py-20">Information for this section will be available soon.</p>
    };

    return (
      <div className="py-24 md:py-32 px-4 md:px-6 max-w-5xl mx-auto animate-fadeIn">
        <button onClick={() => setActivePage('home')} className="inline-flex items-center text-gray-400 hover:text-wedding-600 mb-8 md:mb-12 transition-all group">
          <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
          <span className="font-bold uppercase text-[10px] md:text-xs tracking-widest">Return Home</span>
        </button>
        <div className="mb-10 md:mb-16">
          <div className="w-12 h-12 md:w-16 md:h-16 bg-wedding-100 rounded-2xl flex items-center justify-center text-wedding-600 mb-4 md:mb-6">
             <active.icon className="w-6 h-6 md:w-8 md:h-8" />
          </div>
          <h1 className="text-3xl md:text-7xl font-serif font-black text-gray-900 mb-3 md:mb-4">{active.title}</h1>
          <p className="text-base md:text-xl text-gray-500 font-light">{active.subtitle}</p>
        </div>
        <div className="bg-white border border-wedding-100 rounded-[2rem] md:rounded-[3rem] p-6 md:p-12 shadow-2xl shadow-wedding-100">
           {active.content}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen font-sans bg-wedding-50 selection:bg-wedding-100 selection:text-wedding-900">
      
      {/* Navbar */}
      <header className={`fixed top-0 left-0 w-full z-[100] transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-md py-3 md:py-4 shadow-sm border-b border-wedding-100' : 'py-5 md:py-8'}`}>
        <nav className="max-w-7xl mx-auto px-4 md:px-8 flex justify-between items-center">
          <div className="flex items-center space-x-2 md:space-x-3 cursor-pointer group" onClick={() => setActivePage('home')}>
            <div className="p-1.5 md:p-2 bg-wedding-600 rounded-lg md:rounded-xl shadow-md group-hover:rotate-6 transition-transform">
               <Logo className="w-4 h-4 md:w-6 md:h-6 text-white" />
            </div>
            <span className="text-lg md:text-2xl font-serif font-black tracking-tight text-gray-900">EVENT MANAGER</span>
          </div>
          
          <div className="hidden lg:flex items-center space-x-12">
             {['Features', 'Pricing'].map((item) => (
               <button 
                key={item} 
                onClick={() => setActivePage(item.toLowerCase() as PageId)} 
                className="text-xs font-black uppercase tracking-[0.2em] text-gray-500 hover:text-wedding-600 transition-colors"
               >
                 {item}
               </button>
             ))}
          </div>

          <div className="flex items-center space-x-3 md:space-x-6">
            <div className="relative group hidden sm:block">
              <button className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full border border-wedding-100 text-gray-500 hover:text-wedding-600 transition-all shadow-sm">
                 <span className="text-base">{SUPPORTED_LANGUAGES.find(l => l.code === language)?.flag}</span>
                 <span className="text-[10px] font-black uppercase tracking-widest">{language}</span>
                 <ChevronDown className="w-4 h-4" />
              </button>
              <div className="absolute right-0 mt-3 w-56 max-h-[60vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-wedding-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 z-[100]">
                 <div className="p-2 grid grid-cols-1 gap-1">
                    {SUPPORTED_LANGUAGES.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`w-full text-left px-4 py-3 text-xs rounded-xl hover:bg-wedding-50 flex items-center justify-between transition-colors ${language === lang.code ? 'bg-wedding-50 text-wedding-700 font-bold' : 'text-gray-500'}`}
                      >
                          <span className="flex items-center gap-3">
                            <span className="text-lg">{lang.flag}</span>
                            <span>{lang.name}</span>
                          </span>
                          {language === lang.code && <CheckCircle2 className="w-4 h-4" />}
                      </button>
                    ))}
                 </div>
              </div>
            </div>
            <button onClick={handleShare} className="p-2 md:p-3 bg-white rounded-full border border-wedding-100 text-gray-400 hover:text-wedding-600 transition-all shadow-sm"><Share2 className="w-4 h-4 md:w-5 md:h-5" /></button>
            <button onClick={onGetStarted} className="px-4 md:px-8 py-2 md:py-3 bg-gray-900 text-white rounded-full font-black uppercase tracking-widest text-[9px] md:text-[10px] hover:bg-black transition-all shadow-lg">
              {t('button.login')}
            </button>
          </div>
        </nav>
      </header>

      {activePage === 'home' ? (
        <>
          {/* Hero Section */}
          <section className="relative pt-24 md:pt-48 pb-16 md:pb-32 px-4 md:px-8 overflow-hidden">
            <div className="max-w-5xl mx-auto text-center relative z-10 animate-fadeIn">
               <div className="inline-flex items-center space-x-2 bg-white px-4 md:px-6 py-2 rounded-full shadow-md border border-wedding-100 mb-8 md:mb-12">
                  <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-wedding-500" />
                  <span className="text-[9px] md:text-[11px] font-black uppercase tracking-[0.3em] text-wedding-800">Elite Venue Management Platform</span>
               </div>
               
               <h1 className="text-4xl sm:text-6xl md:text-8xl font-serif font-black mb-6 md:mb-10 leading-[1.2] md:leading-[1.1] text-gray-900 tracking-tight">
                 {t('landing.hero.title.part1')} <span className="text-wedding-600 italic">{t('landing.hero.title.part2')}</span> {t('landing.hero.title.part3')} <span className="text-indigo-600">{t('landing.hero.title.part4')}</span>.
               </h1>
               
               <p className="text-lg md:text-3xl mb-10 md:mb-16 text-gray-500 font-light leading-relaxed max-w-3xl mx-auto">
                 Connecting professional venues with individual visions through simple AI orchestration.
               </p>
               
               <div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center items-center">
                  <button 
                    onClick={onGetStarted} 
                    className="w-full sm:w-auto px-10 md:px-12 py-5 md:py-6 bg-wedding-600 text-white rounded-2xl md:rounded-3xl font-black uppercase tracking-[0.2em] text-xs md:text-sm hover:bg-wedding-700 transition-all shadow-2xl shadow-wedding-200 transform hover:-translate-y-1 flex items-center justify-center group"
                  >
                    {t('button.getStarted')}
                    <ArrowRight className="ml-3 w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button onClick={() => setActivePage('features')} className="w-full sm:w-auto px-10 md:px-12 py-5 md:py-6 bg-white border border-wedding-100 text-gray-700 rounded-2xl md:rounded-3xl font-black uppercase tracking-[0.2em] text-xs md:text-sm hover:bg-wedding-50 transition-all shadow-lg">
                    See Features
                  </button>
               </div>
            </div>
          </section>

          {/* Core Services */}
          <section className="py-20 md:py-32 bg-white border-y border-wedding-100">
             <div className="max-w-7xl mx-auto px-4 md:px-8">
                <div className="text-center mb-12 md:mb-20">
                   <h2 className="text-3xl md:text-5xl font-serif font-black text-gray-900 mb-4">Premier Service Ecosystem</h2>
                   <p className="text-base md:text-xl text-gray-400 font-light max-w-2xl mx-auto">Comprehensive operational modules for the modern celebration lifecycle.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
                   {allServices.map((service, idx) => (
                     <div key={idx} className="p-8 md:p-10 rounded-[2rem] md:rounded-[2.5rem] bg-wedding-50 border border-wedding-100 hover:bg-white hover:shadow-2xl hover:shadow-wedding-100 transition-all group text-center md:text-left">
                        <div className={`w-16 h-16 md:w-20 md:h-20 ${service.bg} ${service.color} rounded-2xl flex items-center justify-center mb-6 md:mb-8 shadow-inner group-hover:scale-110 transition-transform mx-auto md:mx-0`}>
                           <service.icon className="w-8 h-8 md:w-10 md:h-10" />
                        </div>
                        <h3 className="text-xl md:text-2xl font-serif font-black text-gray-900 mb-3 md:mb-4">{service.title}</h3>
                        <p className="text-gray-500 text-sm md:text-lg font-light leading-relaxed">{service.desc}</p>
                     </div>
                   ))}
                </div>
             </div>
          </section>

          {/* Pricing Highlight */}
          <section className="py-20 md:py-32 bg-wedding-50">
            <div className="max-w-6xl mx-auto px-4 md:px-8">
               <div className="text-center mb-12 md:mb-20">
                  <h2 className="text-3xl md:text-5xl font-serif font-black text-gray-900 mb-4 md:mb-6">Investment Plans</h2>
                  <div className="inline-flex items-center gap-3 md:gap-4 bg-white px-6 md:px-8 py-2 md:py-3 rounded-full border border-wedding-100 shadow-sm mt-4">
                     <Globe className="w-4 h-4 md:w-5 md:h-5 text-wedding-500" />
                     <select value={pricingCountry} onChange={(e) => setPricingCountry(e.target.value)} className="bg-transparent font-black text-[10px] md:text-xs uppercase tracking-widest outline-none cursor-pointer text-gray-700">
                       {COUNTRIES_CURRENCIES.map(c => <option key={c.country} value={c.country}>{c.country}</option>)}
                     </select>
                  </div>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                  <div className="bg-white p-10 md:p-16 rounded-[2rem] md:rounded-[3rem] shadow-2xl border border-wedding-100 flex flex-col items-center text-center group hover:-translate-y-2 transition-transform">
                     <h3 className="text-2xl md:text-3xl font-serif font-bold text-gray-800 mb-4">Individual Planner</h3>
                     <div className="flex items-baseline mb-8 md:mb-12">
                        <span className="text-5xl md:text-7xl font-black text-gray-900 tracking-tighter">{selectedCurrencyConfig.symbol}{localIndPrice}</span>
                        <span className="text-gray-400 ml-2 md:ml-4 font-black text-[10px] md:text-xs uppercase tracking-widest">/ Next Event</span>
                     </div>
                     <ul className="space-y-4 md:space-y-6 mb-10 md:mb-16 text-left w-full">
                        {['1st Event Completely Free', 'AI Theme & Vision Engine', 'Verified Vendor Network', 'Digital Budget Intelligence'].map(f => (
                          <li key={f} className="flex items-center text-base md:text-lg text-gray-500 font-light">
                             <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-wedding-500 mr-3 md:mr-4 flex-shrink-0" />
                             {f}
                          </li>
                        ))}
                     </ul>
                     <button onClick={onGetStarted} className="w-full py-5 md:py-6 bg-wedding-600 text-white rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] md:text-xs shadow-xl hover:bg-wedding-700 transition-all">Start Planning</button>
                  </div>

                  <div className="bg-gray-900 p-10 md:p-16 rounded-[2rem] md:rounded-[3rem] shadow-2xl text-white flex flex-col items-center text-center relative overflow-hidden group hover:-translate-y-2 transition-transform">
                     <div className="absolute top-0 right-0 w-48 h-48 bg-wedding-600/20 blur-3xl rounded-full -mr-24 -mt-24"></div>
                     <h3 className="text-2xl md:text-3xl font-serif font-bold mb-4 text-white">Venue Enterprise</h3>
                     <div className="flex items-baseline mb-8 md:mb-12">
                        <span className="text-5xl md:text-7xl font-black tracking-tighter">{selectedCurrencyConfig.symbol}{localBizPrice}</span>
                        <span className="text-gray-500 ml-2 md:ml-4 font-black text-[10px] md:text-xs uppercase tracking-widest">/ Monthly</span>
                     </div>
                     <ul className="space-y-4 md:space-y-6 mb-10 md:mb-16 text-left w-full">
                        {['Master CRM Dashboard', 'AI Client Outreach drafts', 'Verified Global Visibility', 'Lead Tracking Analytics'].map(f => (
                          <li key={f} className="flex items-center text-base md:text-lg text-gray-300 font-light">
                             <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-wedding-400 mr-3 md:mr-4 flex-shrink-0" />
                             {f}
                          </li>
                        ))}
                     </ul>
                     <button onClick={onGetStarted} className="w-full py-5 md:py-6 bg-white text-gray-900 rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] md:text-xs shadow-xl hover:bg-gray-100 transition-all">Go Professional</button>
                  </div>
               </div>
            </div>
          </section>
        </>
      ) : renderStaticPage(activePage)}

      {/* Footer */}
      <footer className="bg-white py-16 md:py-24 px-4 md:px-8 border-t border-wedding-100">
         <div className="max-w-7xl mx-auto flex flex-col lg:flex-row justify-between items-start gap-12 md:gap-16">
            <div className="space-y-6 md:space-y-10 max-w-sm">
               <div className="flex items-center space-x-3 cursor-pointer" onClick={() => setActivePage('home')}>
                  <div className="p-2 bg-wedding-600 rounded-xl">
                    <Logo className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </div>
                  <span className="text-2xl md:text-3xl font-serif font-black tracking-tighter text-gray-900 uppercase">Event Manager</span>
               </div>
               <p className="text-base md:text-lg text-gray-400 font-light leading-relaxed">Elegant event management for the modern visionary. Connecting venues and people worldwide through sophisticated intelligence.</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-10 md:gap-16 flex-1 md:justify-items-end w-full">
               <div className="space-y-4 md:space-y-6 text-left md:text-right">
                  <h4 className="text-[9px] md:text-[10px] font-black text-gray-900 uppercase tracking-[0.3em] mb-4 md:mb-8">Product</h4>
                  <ul className="space-y-3 md:space-y-4 text-gray-400 text-[9px] md:text-[10px] font-black uppercase tracking-widest">
                     <li onClick={() => setActivePage('features')} className="hover:text-wedding-600 cursor-pointer transition-colors">Features</li>
                     <li onClick={() => setActivePage('integrations')} className="hover:text-wedding-600 cursor-pointer transition-colors">Integrations</li>
                     <li onClick={() => setActivePage('pricing')} className="hover:text-wedding-600 cursor-pointer transition-colors">Pricing</li>
                     <li onClick={() => setActivePage('updates')} className="hover:text-wedding-600 cursor-pointer transition-colors">Updates</li>
                  </ul>
               </div>
               <div className="space-y-4 md:space-y-6 text-left md:text-right">
                  <h4 className="text-[9px] md:text-[10px] font-black text-gray-900 uppercase tracking-[0.3em] mb-4 md:mb-8">Resources</h4>
                  <ul className="space-y-3 md:space-y-4 text-gray-400 text-[9px] md:text-[10px] font-black uppercase tracking-widest">
                     <li onClick={() => setActivePage('help-center')} className="hover:text-wedding-600 cursor-pointer transition-colors">Help Center</li>
                     <li onClick={() => setActivePage('community')} className="hover:text-wedding-600 cursor-pointer transition-colors">Community</li>
                     <li onClick={() => setActivePage('blog')} className="hover:text-wedding-600 cursor-pointer transition-colors">Blog</li>
                     <li onClick={() => setActivePage('guides')} className="hover:text-wedding-600 cursor-pointer transition-colors">Guides</li>
                  </ul>
               </div>
               <div className="space-y-4 md:space-y-6 text-left md:text-right">
                  <h4 className="text-[9px] md:text-[10px] font-black text-gray-900 uppercase tracking-[0.3em] mb-4 md:mb-8">Company</h4>
                  <ul className="space-y-3 md:space-y-4 text-gray-400 text-[9px] md:text-[10px] font-black uppercase tracking-widest">
                     <li onClick={() => setActivePage('about-us')} className="hover:text-wedding-600 cursor-pointer transition-colors">About Us</li>
                     <li onClick={() => setActivePage('privacy')} className="hover:text-wedding-600 cursor-pointer transition-colors">Privacy</li>
                     <li onClick={() => setActivePage('terms')} className="hover:text-wedding-600 cursor-pointer transition-colors">Terms</li>
                  </ul>
               </div>
            </div>
         </div>
         <div className="max-w-7xl mx-auto pt-10 md:pt-16 mt-10 md:mt-20 border-t border-wedding-50 flex flex-col sm:flex-row justify-between items-center gap-6 md:gap-8 text-[9px] md:text-[10px] font-black text-gray-300 uppercase tracking-[0.4em] text-center sm:text-left">
            <span>&copy; {new Date().getFullYear()} Event Manager Pro</span>
            <div className="flex gap-8 md:gap-12">
               <span>Secured Deployment Node V3.4</span>
            </div>
         </div>
      </footer>
    </div>
  );
};

export default LandingPage;
